import MenuButton from 'apollo-react/components/MenuButton';
import qs from 'query-string';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import BulkImportHistoryModal from './BulkImportHistory';
import MasterSetupImportModal from './BulkImportModal';
import DownloadTemplateModal from './DownloadTemplateModal';
import TemplateListModal from './TemplateListModal';
import { downloadBulkImportFile, setImportModalOpen } from '../../actions/BulkImportActions';
import { BULK_IMPORT_APIS, ENTITY_NAMES } from '../../constants/MasterSetupConstants';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { getIsRtlLanguage } from '../../selectors/AuthSelectors';
import { getIsImportModalOpen, getStepIdProp } from '../../selectors/BulkImportSelectors';
import { getUITA } from '../../utils/commonUtils';

const BulkImportActionsMenu = ({ isWorkflowMasterSetup, entityName }) => {
  const [isTemplateListModalOpen, setTemplateListModalOpen] = useState(false);
  const [isHistoryModalOpen, setHistoryModalOpen] = useState(false);
  const [isDownloadModalOpen, setDownloadTemplateModalOpen] = useState(false);
  const [apiParams, setApiParams] = useState({});

  const isRtlLanguage = useSelector(getIsRtlLanguage);
  const stepId = useSelector(getStepIdProp(entityName));
  const isImportModalOpen = useSelector(getIsImportModalOpen);

  const { search } = useLocation();
  const translateString = useTranslation();
  const dispatch = useDispatch();

  const { containerWorkflowKey, workflowKey } = qs.parse(search);

  const workflowTemplateProps = {
    createStepId: stepId,
    createContainerWorkflowKey: containerWorkflowKey,
    createWorkflowKey: workflowKey,
  };

  const downloadTemplate = (params) => {
    dispatch(
      downloadBulkImportFile({
        downloadType: BULK_IMPORT_APIS.DOWNLOAD_TEMPLATE,
        isWorkflowMasterSetup,
        apiProps: params,
      })
    );
  };

  const defaultBulkImportButtons = [
    {
      text: translateString('Import History'),
      onClick: () => setHistoryModalOpen(true),
    },
    {
      text: translateString('Import'),
      onClick: () => dispatch(setImportModalOpen(true)),
    },
  ];

  const submissionCodeBulkImportButtons = [
    {
      text: translateString('Import Product Codes'),
      onClick: () => dispatch(setImportModalOpen(true)),
    },
  ];

  const migrateInternalActorTemplateButton = {
    text: translateString('Download Migrate Actors to Person Template'),
    onClick: () =>
      downloadTemplate({
        entityKey: 'IAC_INTERNAL_ACTOR',
        actionName: 'MigrateInternalActor',
        ...workflowTemplateProps,
      }),
  };

  const downloadMasterSetupTemplateButton = {
    text: translateString('Download Template'),
    onClick: () => {
      setApiParams({ entityKey: entityName });
      setDownloadTemplateModalOpen(true);
    },
  };

  const entityTemplatesButton = {
    text: translateString('Templates'),
    onClick: () => setTemplateListModalOpen(true),
  };

  const bulkImportButtons =
    entityName === 'IAC_INTERNAL_ACTOR'
      ? [migrateInternalActorTemplateButton, ...defaultBulkImportButtons, entityTemplatesButton]
      : isWorkflowMasterSetup
      ? [...defaultBulkImportButtons, entityTemplatesButton]
      : entityName === ENTITY_NAMES.SUBMISSION_CODE
      ? [...submissionCodeBulkImportButtons]
      : [downloadMasterSetupTemplateButton, ...defaultBulkImportButtons];

  return (
    <>
      <MenuButton
        buttonText={translateString('Bulk Import Actions')}
        style={{ marginRight: 10, marginLeft: isRtlLanguage && 10 }}
        menuItems={bulkImportButtons}
        ui-ta={getUITA('bulk-import-actions')}
      />
      <TemplateListModal
        open={isTemplateListModalOpen}
        setModalOpen={setTemplateListModalOpen}
        entityName={entityName}
        workflowTemplateProps={workflowTemplateProps}
        setDownloadTemplateModalOpen={setDownloadTemplateModalOpen}
        setApiParams={setApiParams}
      />
      {isImportModalOpen && (
        <MasterSetupImportModal
          entityName={entityName}
          entityViewName={`V_${entityName}`}
          isWorkflowMasterSetup={isWorkflowMasterSetup}
        />
      )}
      <BulkImportHistoryModal
        setModalOpen={setHistoryModalOpen}
        open={isHistoryModalOpen}
        importEntityKey={isWorkflowMasterSetup ? entityName : `V_${entityName}`}
        isWorkflowMasterSetup={isWorkflowMasterSetup}
      />
      {isDownloadModalOpen && (
        <DownloadTemplateModal
          setModalOpen={setDownloadTemplateModalOpen}
          apiParams={apiParams}
          isWorkflowMasterSetup={isWorkflowMasterSetup}
        />
      )}
    </>
  );
};

export default BulkImportActionsMenu;
